import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, Send, CheckCircle2 } from "lucide-react";
import CitizenAttachment from "@/components/CitizenAttachment";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPLAINT_FORM } from "@/constants/testIds";

const ComplaintForm = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    complaint_type: "",
    description: "",
    photo_url: "",
    location: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/contact/complaint-types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Informe seu nome completo.";
    if (form.phone.trim().length < 8) err.phone = "Informe um telefone válido.";
    if (!form.complaint_type) err.complaint_type = "Selecione o tipo.";
    if (form.description.trim().length < 5) err.description = "Descreva a reclamação (mínimo 5 caracteres).";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não disponível.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update("location", `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
        toast.success("Localização capturada.");
      },
      () => toast.error("Não foi possível obter a localização.")
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/contact/complaint", { ...form, photo_url: form.photo_url || null });
      setSuccess(true);
    } catch (err) {
      toast.error("Erro ao enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-[var(--fi-blue)] mb-4 hover:text-[var(--fi-red)] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Voltar
      </button>

      <div className="mb-2 text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold">
        Formulário
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-[0.9] mb-2">
        Enviar Reclamação
      </h1>
      <p className="text-gray-600 mb-6 max-w-lg">
        Preencha os dados para nossa equipe registrar e apurar a sua reclamação.
      </p>

      <form onSubmit={onSubmit} className="space-y-4 bg-white rounded-2xl p-5 sm:p-6 shadow-[0_8px_30px_rgba(13,27,75,0.08)]">
        <Field label="Nome completo *" error={errors.name}>
          <input
            data-testid={COMPLAINT_FORM.name}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="input"
            placeholder="Como você se chama?"
          />
        </Field>
        <Field label="Telefone / WhatsApp *" error={errors.phone}>
          <input
            data-testid={COMPLAINT_FORM.phone}
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className="input"
            placeholder="(19) 9 9999-9999"
          />
        </Field>
        <Field label="Tipo de reclamação *" error={errors.complaint_type}>
          <select
            data-testid={COMPLAINT_FORM.type}
            value={form.complaint_type}
            onChange={(e) => update("complaint_type", e.target.value)}
            className="input bg-white"
          >
            <option value="">Selecione uma opção...</option>
            {types.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
        <Field label="Descrição *" error={errors.description}>
          <textarea
            data-testid={COMPLAINT_FORM.description}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            rows={5}
            className="input"
            placeholder="Descreva o problema com o máximo de detalhes possível..."
          />
        </Field>
        <Field label="Localização (opcional)">
          <div className="flex gap-2">
            <input
              data-testid={COMPLAINT_FORM.location}
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              className="input flex-1"
              placeholder="Endereço ou coordenadas"
            />
            <button
              type="button"
              onClick={useGPS}
              className="px-3 rounded-xl border border-gray-200 hover:border-[var(--fi-blue)] text-[var(--fi-blue)] transition-colors flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
            >
              <MapPin className="w-4 h-4" />
              GPS
            </button>
          </div>
        </Field>
        <Field label="Foto (opcional)">
          <CitizenAttachment
            testId={COMPLAINT_FORM.photo}
            label="Anexar foto (câmera ou galeria)"
            value={form.photo_url ? { url: form.photo_url } : null}
            onChange={(att) => update("photo_url", att?.url || "")}
          />
        </Field>

        <button
          type="submit"
          data-testid={COMPLAINT_FORM.submit}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-sm py-4 transition-colors disabled:opacity-70"
        >
          {submitting ? "Enviando..." : (<><Send className="w-4 h-4" /> Enviar Reclamação</>)}
        </button>
      </form>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s ease;
          font-family: inherit;
        }
        .input:focus { border-color: #0D1B4B; }
      `}</style>

      {success && (
        <SuccessModal
          testId={COMPLAINT_FORM.successModal}
          closeTestId={COMPLAINT_FORM.successClose}
          onClose={() => navigate("/")}
        />
      )}
    </section>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
      {label}
    </label>
    {children}
    {error && <div className="text-xs text-[var(--fi-red)] mt-1">{error}</div>}
  </div>
);

export const SuccessModal = ({ testId, closeTestId, onClose }) => (
  <div
    data-testid={testId}
    className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
  >
    <div className="w-full max-w-sm bg-white rounded-2xl p-6 text-center shadow-2xl">
      <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto flex items-center justify-center">
        <CheckCircle2 className="w-10 h-10 text-emerald-500" strokeWidth={2.4} />
      </div>
      <div className="font-display text-2xl font-extrabold uppercase text-[var(--fi-blue)] mt-4">
        Recebemos!
      </div>
      <p className="text-sm text-gray-600 mt-2">
        Sua mensagem foi enviada. Nossa equipe entrará em contato em breve.
      </p>
      <button
        data-testid={closeTestId}
        onClick={onClose}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--fi-blue)] text-white font-bold uppercase tracking-widest text-sm py-3 hover:bg-[var(--fi-blue-2)] transition-colors"
      >
        Voltar ao início
      </button>
    </div>
  </div>
);

export default ComplaintForm;
